import React, { useEffect, useMemo, useState } from "react";
import Modal from "@components/atoms/Modal";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Input, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import * as yup from "yup";
import { Dimensions, Platform, View, StyleSheet } from "react-native";
import { useUpdateOrderRequest, WorkFlowActions } from "@apis/requestHistory";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

type DeclineRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  stockItemRequest: any;
};

const COMMENT = "comment";

const schema = yup
  .object({
    comment: yup.string().required(),
  })
  .required();

const DeclineRequestModal = ({
  isOpen,
  onClose,
  onSuccess,
  stockItemRequest,
}: DeclineRequestModalProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit, getValues } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      comment: stockItemRequest?.salesperson_comment ?? "",
    },
  });
  const { theme, typography } = useScreenOptionsHooks();
  const isWebResponsiveView = Platform.OS === "web" && !theme.isLargeScreen;
  const useFixedSheetLayout =
    Platform.OS === "web" ? isWebResponsiveView : !theme.isTablet;
  const deviceHeight = Dimensions.get("window").height;
  const webMinModalHeight = useMemo(() => {
    if (!isOpen) return undefined;
    const height = Dimensions.get("window").height;
    return theme.isTablet ? height * 0.6 : height * 0.8;
  }, [isOpen, theme.isTablet]);
  const minModalHeight = isWebResponsiveView
    ? webMinModalHeight
    : theme.isTablet
    ? deviceHeight * 0.3
    : deviceHeight * 0.8;
  const isNativeTablet = Platform.OS !== "web" && theme.isTablet;
  const [isCommentEmpty, setIsCommentEmpty] = useState(
    !(stockItemRequest?.salesperson_comment ?? "").trim()
  );

  useEffect(() => {
    if (isOpen) {
      setIsCommentEmpty(!(stockItemRequest?.salesperson_comment ?? "").trim());
    }
  }, [isOpen, stockItemRequest?.salesperson_comment]);

  const [isEventLoading, setEventLoading] = useState(false);
  const updateStatus = useUpdateOrderRequest(stockItemRequest.id);

  const onSubmit = () => {
    updateStatus.mutate(
      {
        workflow_action: WorkFlowActions.REQUEST_DECLINE,
        salesperson_comment: getValues(COMMENT),
      },
      {
        onSuccess() {
          setEventLoading(false);
          onSuccess?.();
          showToast(
            undefined,
            t("requestHistory.requestSent.text", "Request sent successfully"),
            ToastTypes.SUCCESS,
            true,
            true
          );
        },
        onError(errorData: any) {
          setEventLoading(false);
          onClose?.();
          const errorMessage =
            errorData?.response?.data?.response?.header?.message;
          if (errorMessage) {
            showToast(undefined, errorMessage, ToastTypes.ERROR, true, true);
          }
        },
      }
    );
  };

  if (!isOpen) return null;

  const formContent = (
    <>
      <Spacer size={SpaceType.large} />
      <VDBText color={theme["core-05"]} type={typography["Body-R"]}>
        {t(
          "requestHistoryDetail.declineRequest.message.text",
          "Add a comments for a decline request. The admin will review and decide."
        )}
      </VDBText>

      <Spacer size={SpaceType.Double} />

      <Controller
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <Input
            inputLabel={t("requestHistoryDetail.comment.title.text", "Comment")}
            placeholder={t(
              "requestHistoryDetail.comment.title.text",
              "Comment"
            )}
            hideIconContainer={true}
            onBlur={onBlur}
            onChangeText={(text) => {
              const trimmed = text.trimStart();
              onChange(trimmed);
              const empty = !trimmed;
              if (empty !== isCommentEmpty) {
                setIsCommentEmpty(empty);
              }
            }}
            value={value}
            multiline={true}
            height={"60"}
          />
        )}
        name={"comment"}
      />
    </>
  );

  const submitButton = (
    <Button
      loading={isEventLoading}
      type={ButtonType.primary}
      disabled={isCommentEmpty}
      category={ButtonCategory.default}
      title={t(
        "requestHistoryDetail.declineRequest.button.sendRequest.text",
        "Send Request"
      )}
      onPress={() => {
        setEventLoading(true);
        handleSubmit(onSubmit)();
      }}
    />
  );

  const modalTitle = t(
    "requestHistoryDetail.declineRequest.title.text",
    "Decline"
  );

  if (isNativeTablet) {
    return (
      <Modal
        headerTitle={modalTitle}
        isOpen={isOpen}
        onClose={() => {
          onClose();
        }}
        type="sheet"
      >
        <View style={styles.tabletSheetContainer}>
          <KeyboardAwareScrollView
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.tabletContent}>{formContent}</View>
          </KeyboardAwareScrollView>

          <View style={styles.tabletContentButton}>
            {submitButton}
            <Spacer size={SpaceType.Extra} />
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal
      headerTitle={modalTitle}
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      paddingLeft={16}
      paddingRight={16}
      paddingBottom={40}
      paddingTop={16}
      minModalHeight={minModalHeight}
      skipKeyboardAware={useFixedSheetLayout}
      fillContentHeight={useFixedSheetLayout}
      allowFullScreen={isWebResponsiveView}
    >
      <View style={styles.content}>
        <View
          style={[
            styles.formSection,
            { width: theme.isTablet ? 343 : "100%", alignSelf: "center" },
          ]}
        >
          {formContent}
        </View>

        {Platform.OS === "web" && <Spacer size={SpaceType.Size_48} />}

        <View
          style={{ width: theme.isTablet ? 608 : "100%", alignSelf: "center" }}
        >
          {submitButton}
          <Spacer size={SpaceType.Extra} />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    minHeight: 0,
  },
  formSection: {
    flex: 1,
    minHeight: 0,
  },
  tabletSheetContainer: {
    flex: 1,
  },
  tabletContent: {
    width: 343,
    paddingBottom: 220,
    alignSelf: "center",
  },
  tabletContentButton: {
    width: 608,
    alignSelf: "center",
    paddingBottom: 24,
  },
});

export default DeclineRequestModal;
